import React from 'react';
import { TransactionList as UITransactionList } from '@shopee/chatbot-ui';
import { TransactionListProps as UITransactionListProps } from '@shopee/chatbot-ui/lib/components/TransactionList';
import { TransactionProps as UITransactionProps } from '@shopee/chatbot-ui/lib/components/Transaction';

import {
  INPUT_TYPE,
  Transaction as TransactionTS,
  TRACKING_SECTION,
  TRACKING_TYPE,
  TRACKING_TARGET,
  ReportSelectButtonId,
  ReportListButtonType,
  ReportSelectButtonType,
  ReportSelectMoreButtonId,
  ReportSelectMoreButtonType,
  ReportListButtonId,
  TRACKING_MSG_TYPE,
  ReportStillNotFoundButtonId,
  ReportStillNotFoundButtonType,
  RuleBasedMatchOptionTriggeredType,
} from '@/utils/constants';
import { assignParams } from '@/utils/dataTypeConvert';
import { TransactionItemType } from '@/types/Transaction';
import { getTrans, formatPrice } from '@/utils/tools';
import { ChatbotUIBasicProps } from '@/types/common';
import { USER_SEND_TYPE } from '@/types/history';
import { ButtonAction } from '@/utils/getButtonData';
import { REPLY_TYPE } from '@/utils/responseHandler';

export enum TransactionType {
  Topup = 2,
  Withdrawal,
}

const TransStatusArr = {
  1: 'pending',
  2: 'pending',
  3: 'success',
  4: 'fail',
  5: 'fail',
  6: 'fail',
} as Record<number, string>;

export type TransactionListProps = UITransactionListProps & {
  selectOtherAction: any;
  selectAction: any;
  defaultStatus: string;
  taskbotInfo: any;
  statusText: string;
} & ChatbotUIBasicProps;

export function getTitle(type: TransactionType, transaction: any) {
  if (type === TransactionType.Topup) {
    // @MARK transify key
    return `${getTrans(TransactionTS.labelTopupPrefix, 'From')} ${transaction.payment_channel_text}`;
  }

  if (type === TransactionType.Withdrawal) {
    // @MARK transify key
    return `${getTrans(TransactionTS.labelWithdrawalPrefix, 'To Bank')} ${transaction.bank_name}`;
  }
}

export function mapDataToTransaction(
  type: TransactionType,
  transaction: TransactionItemType,
  statusText: Record<number, string>,
): UITransactionProps {
  return {
    sn: transaction.sn,
    title: getTitle(type, transaction) || '',
    amount: `${type === TransactionType.Topup ? '+' : '-'}${formatPrice(transaction.amount)}`,
    specification: transaction.create_time,
    status: statusText[transaction.status],
    type: TransStatusArr[transaction.status] as UITransactionProps['type'],
  };
}

interface loadMoreParams {
  page_num: number;
  page_size: number;
  status: number;
}

const TransactionList = (props: TransactionListProps) => {
  const { selectAction, type, maxDisplay, statusText, roundId, dialogueId } = props;

  const nextParams = {
    session_id: props.sessionId,
    dialogue_id: props.dialogueId,
    thread_id: props.threadId,
    thread_id_v2: props.threadIdV2,
    round_id: props.roundId,
    input_type: INPUT_TYPE.BUTTON,
    thread_node_id: props.threadNodeId,
  };

  // transaction是全量返回，所以需要计算hasMoreTransaction
  const hasMore = maxDisplay ? props.content?.length > maxDisplay : false;
  const sendType
    = type === TransactionType.Topup
      ? USER_SEND_TYPE.TRANSACTION_SELECT_TOPUP
      : USER_SEND_TYPE.TRANSACTION_SELECT_WITHDRAWL;
  const content = props.content?.map((item: any, index: number) => {
    return {
      ...mapDataToTransaction(type, item, statusText),
      action: {
        info: { ...mapDataToTransaction(type, item, statusText) },
        func: 'showTransaction',
        nextParams: {
          ...nextParams,
          message_text: item.title,
          message_extra: {
            node_id: props.threadNodeId,
            task_bot_action: {
              ...selectAction,
              ...{ params: assignParams(props.selectAction.params, { transaction_sn: item.sn, route_id: item?.route_id }) },
            },
            params: {
              // history message
              user_send: {
                type: sendType,
                [sendType.toLowerCase()]: item,
              },
            },
          },
          triggered_option: {
            triggered_type: RuleBasedMatchOptionTriggeredType.OrderOrTransactionType,
            triggered_index: index,
            round_id: roundId,
            dialogue_id: dialogueId,
            reply_type: REPLY_TYPE.TFE_CARD_SELECTION,
          },
        },
      },
    } as unknown as UITransactionProps;
  });

  const checkHereAction = {
    func: 'next',
    _actualText_: '',
    showUserMessage: true,
    info: { ...nextParams },
    nextParams: {
      ...nextParams,
      message_text: '',
      message_extra: {
        node_id: props.threadNodeId,
        task_bot_action: { ...props.stillNotFoundAction },
      },
    },
    action: {
      is_bypass: props.stillNotFoundAction?.is_bypass,
      action_type: ButtonAction.JumpNode,
      task_bot_action: props.stillNotFoundAction,
    },
    callback_action: {
      action_type: ButtonAction.JumpNode,
      task_bot_action: props.stillNotFoundAction,
    },
    extra: { ...nextParams },
  };

  const loadMore = async (loadMoreProps: loadMoreParams) => {
    const { page_size, page_num } = loadMoreProps;

    return { data: content.slice(page_size * page_num, page_size * page_num + page_size) };
  };

  const trackingProps = {
    componentTrackingData: {
      transaction: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.SHOPEE_PAY_TRANSACTION_CARD],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.SHOPEE_PAY_TRANSACTION_CARD],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          card_type: type === TransactionType.Topup ? 'top_up' : 'withdraw',
          node_point_id: props.nodePointId,
          button_text: props.selectTransactionText,
          button_id: ReportSelectButtonId,
          button_type: ReportSelectButtonType,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
        trackingItemData: (data: any) => ({
          location: data.index,
          transaction_id: data?.transaction?.sn,
        }),
      },
      moreTransaction: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.SHOPEE_PAY_OTHER_TRANSACTION],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.SHOPEE_PAY_OTHER_TRANSACTION],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          node_point_id: props.nodePointId,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          button_id: ReportSelectMoreButtonId,
          button_type: ReportSelectMoreButtonType,
          location: content?.length ?? 0.0,
          button_text: props.loadMoreTransactionText,
          card_type: type === TransactionType.Topup ? 'top_up' : 'withdraw',
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
      },
      donNotSeeTransaction: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.SHOPEE_PAY_DONT_SEE_BUTTON],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.SHOPEE_PAY_DONT_SEE_BUTTON],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          card_type: type === TransactionType.Topup ? 'top_up' : 'withdraw',
          node_point_id: props.nodePointId,
          button_text: props.checkHereContent,
          button_id: ReportSelectMoreButtonId,
          button_type: ReportSelectMoreButtonType,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          location: maxDisplay,
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
      },
      panelDoNotSeeTransaction: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.SHOPEE_PAY_PANEL_DONT_SEE_BUTTON],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.SHOPEE_PAY_PANEL_DONT_SEE_BUTTON],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          card_type: type === TransactionType.Topup ? 'top_up' : 'withdraw',
          node_point_id: props.nodePointId,
          button_text: props.stillNotFoundContent,
          button_id: ReportStillNotFoundButtonId,
          button_type: ReportStillNotFoundButtonType,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
      },
      panelTransaction: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.SHOPEE_PAY_PANEL_TRANSACTION_CARD],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.SHOPEE_PAY_PANEL_TRANSACTION_CARD],
        trackingdataBasic: {
          node_point_id: props.nodePointId,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          button_id: ReportListButtonId,
          button_type: ReportListButtonType,
          card_type: type === TransactionType.Topup ? 'top_up' : 'withdraw',
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
        trackingItemData: (data: any) => ({
          location: data.index,
          transaction_id: data?.transaction?.sn,
        }),
      },
      transactionList: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_TRANSACTION_LIST_SCROLL],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_TRANSACTION_LIST_SCROLL],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          node_point_id: props.nodePointId,
          kb_bot_id: props.kbBotId,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
          card_type: type === TransactionType.Topup ? 'top_up' : 'withdraw',
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
        },
      },
      closePanel: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_TRANSACTION_LIST_CLOSE_PANEL],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_TRANSACTION_LIST_CLOSE_PANEL],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          node_point_id: props.nodePointId,
          kb_bot_id: props.kbBotId,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          task_bot_name: props.taskbotName,
          card_type: type === TransactionType.Topup ? 'top_up' : 'withdraw',
        },
      },
    },
  };

  return (
    <UITransactionList<TRACKING_SECTION, TRACKING_TARGET>
      {...props}
      content={content}
      checkHereAction={checkHereAction as any}
      {...trackingProps}
      onLoadMore={loadMore}
      hasMoreTransaction={hasMore}
    />
  );
};

export default TransactionList;
